import type { OutputBlockData, OutputData } from "@editorjs/editorjs";
import { getEmptyRichEditorData } from "@/features/rich-editor";

const BLOCK_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "UL",
  "OL",
  "BLOCKQUOTE",
  "HR",
  "FIGURE",
  "IMG",
  "IFRAME",
  "TABLE",
]);

const buildPreviewFallback = (emptyText: string) =>
  `<p style="color:#71717a;padding:0 20px 24px;">${emptyText}</p>`;

const buildListBlock = (
  element: HTMLUListElement | HTMLOListElement,
): OutputBlockData<string, Record<string, unknown>> | null => {
  const items = Array.from(element.querySelectorAll(":scope > li"))
    .map((item) => item.innerHTML.trim())
    .filter(Boolean);

  if (items.length === 0) {
    return null;
  }

  return {
    type: "list",
    data: {
      style: element.tagName === "OL" ? "ordered" : "unordered",
      items,
    },
  };
};

const buildQuoteBlock = (
  element: HTMLQuoteElement,
): OutputBlockData<string, Record<string, unknown>> | null => {
  const caption =
    element.querySelector("cite")?.textContent?.trim() ||
    element.querySelector("figcaption")?.textContent?.trim() ||
    "";
  const clone = element.cloneNode(true) as HTMLElement;

  clone.querySelectorAll("cite, figcaption").forEach((node) => {
    node.remove();
  });

  const text = clone.innerHTML.trim();

  if (!text) {
    return null;
  }

  return {
    type: "quote",
    data: {
      text,
      caption,
    },
  };
};

const buildImageBlock = (
  element: HTMLImageElement,
  caption = "",
): OutputBlockData<string, Record<string, unknown>> | null => {
  const url = element.getAttribute("src")?.trim();

  if (!url) {
    return null;
  }

  return {
    type: "image",
    data: {
      file: {
        url,
        fileId: element.getAttribute("data-file-id") ?? undefined,
      },
      caption,
      withBorder: false,
      withBackground: false,
      stretched: false,
    },
  };
};

const buildEmbedBlock = (
  element: HTMLIFrameElement,
  caption = "",
): OutputBlockData<string, Record<string, unknown>> | null => {
  const url = element.getAttribute("src")?.trim();

  if (!url) {
    return null;
  }

  return {
    type: "embed",
    data: {
      service: "custom",
      source: url,
      embed: url,
      caption,
      width: 0,
      height: 0,
    },
  };
};

const buildFigureBlock = (
  element: HTMLElement,
): OutputBlockData<string, Record<string, unknown>> | null => {
  const caption = element.querySelector("figcaption")?.innerHTML.trim() ?? "";
  const image = element.querySelector("img");

  if (image instanceof HTMLImageElement) {
    return buildImageBlock(image, caption);
  }

  const iframe = element.querySelector("iframe");

  if (iframe instanceof HTMLIFrameElement) {
    return buildEmbedBlock(iframe, caption);
  }

  return null;
};

const buildTableBlock = (
  element: HTMLTableElement,
): OutputBlockData<string, Record<string, unknown>> | null => {
  const rows = Array.from(element.querySelectorAll("tr"))
    .map((row) =>
      Array.from(row.querySelectorAll("th, td")).map((cell) =>
        cell.innerHTML.trim(),
      ),
    )
    .filter((row) => row.some(Boolean));

  if (rows.length === 0) {
    return null;
  }

  const hasHeadingRow =
    element.querySelector("thead") !== null ||
    rows[0]?.length ===
      Array.from(element.querySelectorAll("tr:first-child th")).length;

  return {
    type: "table",
    data: {
      withHeadings: hasHeadingRow,
      stretched: true,
      content: rows,
    },
  };
};

const buildParagraphBlocks = (element: Element) => {
  const text = element.innerHTML.trim();

  return text
    ? [
        {
          type: "paragraph",
          data: {
            text,
          },
        },
      ]
    : [];
};

const buildHeaderBlocks = (element: Element, tagName: string) => {
  const text = element.innerHTML.trim();

  return text
    ? [
        {
          type: "header",
          data: {
            text,
            level: Number(tagName.replace("H", "")),
          },
        },
      ]
    : [];
};

const buildDelimiterBlocks = () => [
  {
    type: "delimiter",
    data: {},
  },
];

const buildNestedBlocks = (element: Element) =>
  Array.from(element.children).flatMap((child) => parseElement(child));

const buildWrappedBlockChildren = (element: Element) => {
  const children = Array.from(element.children);

  if (children.length !== 1) {
    return null;
  }

  const [child] = children;

  if (!child || !BLOCK_TAGS.has(child.tagName)) {
    return null;
  }

  return parseElement(child);
};

const parseElement = (
  element: Element,
): OutputBlockData<string, Record<string, unknown>>[] => {
  const tagName = element.tagName;
  const tagHandlers: Partial<
    Record<string, () => OutputBlockData<string, Record<string, unknown>>[]>
  > = {
    P: () => buildParagraphBlocks(element),
    H1: () => buildHeaderBlocks(element, tagName),
    H2: () => buildHeaderBlocks(element, tagName),
    H3: () => buildHeaderBlocks(element, tagName),
    H4: () => buildHeaderBlocks(element, tagName),
    UL: () => {
      const block = buildListBlock(
        element as HTMLUListElement | HTMLOListElement,
      );

      return block ? [block] : [];
    },
    OL: () => {
      const block = buildListBlock(
        element as HTMLUListElement | HTMLOListElement,
      );

      return block ? [block] : [];
    },
    BLOCKQUOTE: () => {
      const block = buildQuoteBlock(element as HTMLQuoteElement);

      return block ? [block] : [];
    },
    HR: buildDelimiterBlocks,
    FIGURE: () => {
      const block = buildFigureBlock(element as HTMLElement);

      return block ? [block] : [];
    },
    IMG: () => {
      const block = buildImageBlock(element as HTMLImageElement);

      return block ? [block] : [];
    },
    IFRAME: () => {
      const block = buildEmbedBlock(element as HTMLIFrameElement);

      return block ? [block] : [];
    },
    TABLE: () => {
      const block = buildTableBlock(element as HTMLTableElement);

      return block ? [block] : [];
    },
  };
  const handler = tagHandlers[tagName];

  if (handler) {
    return handler();
  }

  if (tagName === "DIV") {
    const wrappedBlocks = buildWrappedBlockChildren(element);

    if (wrappedBlocks) {
      return wrappedBlocks;
    }
  }

  if (BLOCK_TAGS.has(tagName)) {
    return [];
  }

  return buildNestedBlocks(element);
};

export const buildLegalPagePreviewDocument = (
  content: string,
  emptyText: string,
) => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 0 0 32px;
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        color: #18181b;
        background:
          radial-gradient(circle at top, rgba(244, 244, 245, 0.9), transparent 38%),
          linear-gradient(180deg, #fcfcfd 0%, #ffffff 24%);
        line-height: 1.5;
        word-break: break-word;
      }

      img,
      iframe,
      video {
        max-width: 100%;
        border: 0;
      }

      table {
        width: 100%;
        border-collapse: collapse;
      }

      a {
        color: #111827;
        text-underline-offset: 0.2em;
      }

      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      figure,
      blockquote {
        margin-block-start: 0;
        margin-block-end: 0;
      }
    </style>
  </head>
  <body>
    ${content || buildPreviewFallback(emptyText)}
  </body>
</html>`;

export const parseLegalPageHtmlToRichEditorData = (
  html: string | null | undefined,
): OutputData => {
  if (!html?.trim()) {
    return getEmptyRichEditorData();
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(html, "text/html");
  const blocks = Array.from(document.body.children).flatMap((child) =>
    parseElement(child),
  );

  return blocks.length > 0 ? { blocks } : getEmptyRichEditorData();
};

const normalizeStructureBlock = (
  block: unknown,
): OutputBlockData<string, Record<string, unknown>> | null => {
  if (!block || typeof block !== "object") {
    return null;
  }

  const candidate = block as {
    type?: unknown;
    data?: unknown;
    text?: unknown;
    level?: unknown;
    items?: unknown;
    style?: unknown;
    caption?: unknown;
    content?: unknown;
    withHeadings?: unknown;
    stretched?: unknown;
    file?: unknown;
    embed?: unknown;
    source?: unknown;
  };
  const type = typeof candidate.type === "string" ? candidate.type : "";

  if (!type) {
    return null;
  }

  if (
    candidate.data &&
    typeof candidate.data === "object" &&
    !Array.isArray(candidate.data)
  ) {
    return {
      type,
      data: candidate.data as Record<string, unknown>,
    };
  }

  switch (type) {
    case "paragraph":
      return {
        type,
        data: {
          text: String(candidate.text ?? ""),
        },
      };
    case "header":
      return {
        type,
        data: {
          text: String(candidate.text ?? ""),
          level: Number(candidate.level ?? 2),
        },
      };
    case "list":
      return {
        type,
        data: {
          style: String(candidate.style ?? "unordered"),
          items: Array.isArray(candidate.items) ? candidate.items : [],
        },
      };
    case "quote":
      return {
        type,
        data: {
          text: String(candidate.text ?? ""),
          caption: String(candidate.caption ?? ""),
        },
      };
    case "table":
      return {
        type,
        data: {
          content: Array.isArray(candidate.content) ? candidate.content : [],
          withHeadings: Boolean(candidate.withHeadings),
          stretched: Boolean(candidate.stretched),
        },
      };
    case "image":
      return {
        type,
        data: {
          file:
            candidate.file && typeof candidate.file === "object"
              ? candidate.file
              : undefined,
          caption: String(candidate.caption ?? ""),
        },
      };
    case "embed":
      return {
        type,
        data: {
          embed: String(candidate.embed ?? ""),
          source: String(candidate.source ?? ""),
          caption: String(candidate.caption ?? ""),
        },
      };
    default:
      return {
        type,
        data: {},
      };
  }
};

export const normalizeLegalPageStructure = (
  structure: unknown,
): OutputData | null => {
  const rawStructure =
    typeof structure === "string"
      ? (() => {
          try {
            return JSON.parse(structure) as unknown;
          } catch {
            return null;
          }
        })()
      : structure;

  if (!rawStructure || typeof rawStructure !== "object") {
    return null;
  }

  const blocks = Array.isArray((rawStructure as { blocks?: unknown }).blocks)
    ? (rawStructure as { blocks: unknown[] }).blocks
        .map((block) => normalizeStructureBlock(block))
        .filter(Boolean)
    : [];

  return blocks.length > 0 ? { blocks } : null;
};
